import { memo, useMemo, useState } from "react"

import { useThreadContext } from "@/providers/ThreadProvider"

import { curatedDoctrineFor, unverifiedCitationsFor } from "../lib/review"
import { isMockReplay } from "../mock/mockMode"
import { useReviewStore } from "../store/review.store"
import { CitationReviewModal } from "./CitationReviewModal"
import { StandardAuthoritiesModal } from "./StandardAuthoritiesModal"

interface CitationReviewChipsProps {
  allegationId: string
  /** "5.N" — passed through to the modal's rewrite hint. */
  sectionHint: string
}

/**
 * Per-allegation citation-review chips (review layer, never order text):
 * unverified citations surface as an actionable chip; the same entry point
 * offers adding a new supporting authority. Renders nothing until a review
 * report exists for the case.
 */
export const CitationReviewChips = memo(function CitationReviewChips({ allegationId, sectionHint }: CitationReviewChipsProps) {
  const report = useReviewStore((s) => s.report)
  const { activeThread } = useThreadContext()
  const [modal, setModal] = useState<null | { addMode: boolean }>(null)
  const [doctrineOpen, setDoctrineOpen] = useState(false)

  const threadId = activeThread?.id ? String(activeThread.id) : ""
  // Memoized: three O(reviews) scans per allegation per render froze the
  // Analysis tab on large review reports (chips render once per allegation
  // row — unmemoized this was O(allegations x reviews) on EVERY re-render).
  const { unverified, doctrine, hasAny } = useMemo(() => {
    if (!report) return { unverified: [], doctrine: [], hasAny: false }
    return {
      unverified: unverifiedCitationsFor(report, allegationId),
      doctrine: curatedDoctrineFor(report, allegationId),
      hasAny: report.citation_reviews.some((c) => c.allegation_id === allegationId),
    }
  }, [report, allegationId])
  if (!report || !threadId) return null
  if (!hasAny && unverified.length === 0) return null

  // MOCK REPLAY: the modals behind these chips search/resolve against the
  // backend — the chips still render (so the review layer lights up during a
  // replay) but stay quietly inert.
  const inert = isMockReplay()

  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
      {doctrine.length > 0 && (
        <button
          className="chip chip--ok"
          disabled={inert}
          onClick={() => !inert && setDoctrineOpen(true)}
          title={
            inert
              ? "Disabled in mock replay"
              : "See the pre-verified standard authorities the pipeline applied for the governing legal standard"
          }
        >
          {doctrine.length} standard authorit{doctrine.length > 1 ? "ies" : "y"} applied
        </button>
      )}
      {unverified.length > 0 && (
        <button
          className="chip chip--run"
          disabled={inert}
          onClick={() => !inert && setModal({ addMode: false })}
          title={
            inert
              ? "Disabled in mock replay"
              : "Open the IndianKanoon search to resolve or discard these citations"
          }
        >
          {unverified.length} cited case{unverified.length > 1 ? "s" : ""} could not be verified —
          review
        </button>
      )}
      <button
        className="chip"
        disabled={inert}
        onClick={() => !inert && setModal({ addMode: true })}
        title={
          inert
            ? "Disabled in mock replay"
            : "Search IndianKanoon and attach a verified supporting authority"
        }
      >
        Add authority
      </button>
      {doctrineOpen && (
        <StandardAuthoritiesModal
          allegationId={allegationId}
          citations={doctrine}
          onClose={() => setDoctrineOpen(false)}
        />
      )}
      {modal && (
        <CitationReviewModal
          threadId={threadId}
          allegationId={allegationId}
          sectionHint={sectionHint}
          citations={unverified}
          addMode={modal.addMode}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  )
})
